<template>
  <InfuseModal :title="t('e_connector.centralSystems.modal.create.title')" @close="emit('close')">
    <form ref="form" id="create-central-system" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[965px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="name"
              :maxlength="255"
              :placeholder="t('e_connector.centralSystems.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.centralSystem.url')"
            class="basis-full"
            size="small"
            :help-text="requireSecureUri ? t('e_connector.centralSystems.modal.field.uri.hint') : undefined"
            :error="uriError"
          >
            <div v-if="requireSecureUri" class="flex w-full">
              <span
                class="flex shrink-0 items-center rounded-l-md border border-r-0 border-neutral-300 bg-neutral-50 px-3 text-xs text-neutral-500"
              >
                {{ WSS_SCHEME }}
              </span>
              <InfuseInput
                class="rounded-l-none"
                size="small"
                v-model="uri"
                :maxlength="249"
                :placeholder="t('e_connector.centralSystems.modal.field.uri.placeholder')"
                :required="true"
              />
            </div>
            <InfuseInput
              v-else
              type="url"
              size="small"
              v-model="uri"
              :maxlength="255"
              :placeholder="t('e_connector.centralSystems.modal.field.uri.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div v-if="!ocpp16WebsocketOnly" class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.type')" class="basis-1/2" size="small">
            <InfuseSelect
              name="type"
              size="small"
              v-model="type"
              :options="typeOptions"
              :label="t('e_connector.centralSystems.modal.field.type.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.centralSystem.ocppVersion')" class="basis-1/2" size="small">
            <InfuseSelect
              name="ocppVersion"
              size="small"
              v-model="ocppVersion"
              :options="ocppVersionOptions"
              :label="t('e_connector.centralSystems.modal.field.ocppVersion.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div v-if="isDomainUser" class="flex items-start gap-4">
          <InfuseField :label="t('common.visibility.label')" class="basis-1/2" size="small">
            <InfuseSelect
              name="visibility"
              size="small"
              v-model="visibility"
              :options="visibilityOptions"
              :format="formatVisibilityLabel"
              :label="t('common.visibility.option.private.label')"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton data-testid="modal-cancel-button" color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="create-central-system"
        :disabled="loading || !isUriValid"
        :loading="loading"
      >
        {{ t('e_connector.common.action.create') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseSelect } from '@infuse/ui/components';
import { computed, inject, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CreateCentralSystem } from '@infuse/api/e-connector/central-system';
import { AbacVisibility } from '@infuse/api/common';
import { useCurrentUserLevel } from '@infuse/iam/composables';
import { ConnectionType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { ConnectorConfig } from '@infuse/e-connector/config/plugin';
import { buildWssUri, stripWssScheme, validateWssHost, WSS_SCHEME } from '@infuse/e-connector/shared/utils';

interface Props {
  loading: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'create', createCentralSystem: CreateCentralSystem): void;
}

withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const { isDomainUser } = useCurrentUserLevel();

const name = ref<string>('');
const uri = ref<string>('');
const type = ref<ConnectionType>(ConnectionType.WEBSOCKET);
const ocppVersion = ref<OcppVersion>(OcppVersion.V16);
const visibility = ref<AbacVisibility | undefined>(undefined);

const requireSecureUri = import.meta.env.VITE_UI_VARIANT_SECURE_BACKOFFICE_URL_ONLY === 'true';
const ocpp16WebsocketOnly = import.meta.env.VITE_UI_VARIANT_BACKOFFICE_OCPP16_WEBSOCKET_ONLY === 'true';

const isUriValid = computed(() => !requireSecureUri || validateWssHost(uri.value) === null);

const uriError = computed(() => {
  if (!requireSecureUri || !uri.value.trim()) return undefined;
  const error = validateWssHost(uri.value);
  return error ? t(`e_connector.centralSystems.modal.field.uri.error.${error}`) : undefined;
});

watch(uri, (value) => {
  if (!requireSecureUri) return;
  const stripped = stripWssScheme(value);
  if (stripped !== value) uri.value = stripped;
});

const config = inject<Ref<ConnectorConfig>>('connectorConfig')?.value;
const typeOptions = Object.values(ConnectionType).filter((v) => {
  switch (v) {
    case ConnectionType.AWS_IOT:
      return !!config?.isAwsIotAvailable;
    case ConnectionType.AWS_SNS:
      return !!config?.isAwsSnsAvailable;
    case ConnectionType.JAVA_API:
      return false;
    default:
      return true;
  }
});
const ocppVersionOptions = Object.values(OcppVersion);
const visibilityOptions: { key: string; value: AbacVisibility }[] = [
  { key: 'PRIVATE', value: AbacVisibility.PRIVATE },
  { key: 'GLOBAL', value: AbacVisibility.GLOBAL }
];

function formatVisibilityLabel(key: string): string {
  return t(`common.visibility.option.${key.toLowerCase()}.label`);
}

function onSave() {
  if (!isUriValid.value) return;

  emit('create', {
    name: name.value,
    uri: requireSecureUri ? buildWssUri(uri.value) : uri.value,
    type: ocpp16WebsocketOnly ? ConnectionType.WEBSOCKET : (type.value as ConnectionType),
    ocppVersion: ocpp16WebsocketOnly ? OcppVersion.V16 : (ocppVersion.value as OcppVersion),
    visibility: visibility.value
  });
}
</script>
